import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  TrendingUp, Target, Users, BarChart2,
  RefreshCw, Copy, CheckCircle, AlertCircle, Sparkles,
  ArrowRight, Lightbulb, MessageCircle, Send,
} from '../../components/icons';
import { MarkdownRenderer } from '../../components/ai/MarkdownRenderer';
import { LoadingSpinner } from '../../components/LoadingSpinner';

// ---------- types ----------

type TabId = 'planner' | 'goals' | 'ai-coach' | 'offer-builder' | 'action-plan' | 'ai-chat';
type Mode = 'coach' | 'partner';
type PlanTier = 'core' | 'growth_os' | 'partner';

interface ProfitGoal {
  id: string;
  coach_id: string;
  mode: Mode;
  plan_tier: PlanTier;
  current_clients: number;
  monthly_price: number;
  monthly_goal: number;
  target_date: string | null;
  audience_type: string | null;
  hours_per_week: number | null;
  preferred_offer_type: string | null;
  generated_strategy: string | null;
  generated_action_plan: string | null;
  offer_name: string | null;
  offer_price: number | null;
  offer_description: string | null;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

// ---------- helpers ----------

const PLAN_COSTS: Record<PlanTier, number> = { core: 99, growth_os: 197, partner: 497 };
const PLAN_INCLUDED: Record<PlanTier, number> = { core: 10, growth_os: 25, partner: 50 };

function calcCoach(clients: number, price: number, tier: PlanTier) {
  const base = PLAN_COSTS[tier];
  const included = PLAN_INCLUDED[tier];
  const extraClients = Math.max(0, clients - included);
  const platformCost = base + extraClients * 10;
  const mrr = clients * price;
  return { mrr, platformCost, margin: mrr - platformCost, breakEven: Math.ceil(platformCost / Math.max(1, price)) };
}

function calcPartner(coaches: number, coachFee: number, avgClients: number, clientFee: number, certFee: number) {
  const totalClients = coaches * avgClients;
  const extraCoaches = Math.max(0, coaches - 10);
  const extraClients = Math.max(0, totalClients - 50);
  const platformCost = 497 + extraCoaches * 29 + extraClients * 5;
  const mrr = coaches * coachFee + totalClients * clientFee;
  return { mrr, platformCost, margin: mrr - platformCost, upfrontRevenue: coaches * certFee, totalClients };
}

function fmt(n: number) {
  return `€${n.toLocaleString('en-EU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

async function callProfitPlan(body: Record<string, unknown>): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  return fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-profit-plan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
    body: JSON.stringify(body),
  });
}

// ---------- shared ui ----------

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function MilestoneCard({ goal, current, price }: { goal: number; current: number; price: number }) {
  const needed = Math.max(0, Math.ceil((goal - current) / Math.max(1, price)));
  const reached = current >= goal;
  return (
    <div className={`rounded-2xl border p-4 text-center ${reached ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
      <p className="text-lg font-bold text-gray-800">{fmt(goal)}/mo</p>
      {reached ? (
        <p className="text-xs text-green-600 font-semibold mt-1">Reached!</p>
      ) : (
        <p className="text-xs text-gray-500 mt-1">{needed} more client{needed !== 1 ? 's' : ''} at {fmt(price)}/mo</p>
      )}
    </div>
  );
}

// ---------- Tab: Revenue Planner ----------

function RevenuePlannerTab() {
  const [mode, setMode] = useState<Mode>('coach');
  const [tier, setTier] = useState<PlanTier>('core');
  const [clients, setClients] = useState(5);
  const [price, setPrice] = useState(99);
  const [setupFee, setSetupFee] = useState(0);
  const [packageFee, setPackageFee] = useState(0);
  const [coaches, setCoaches] = useState(5);
  const [coachFee, setCoachFee] = useState(99);
  const [avgClients, setAvgClients] = useState(6);
  const [clientFee, setClientFee] = useState(10);
  const [certFee, setCertFee] = useState(997);

  const coachCalc = calcCoach(clients, price, tier);
  const partnerCalc = calcPartner(coaches, coachFee, avgClients, clientFee, certFee);
  const calc = mode === 'coach' ? coachCalc : { mrr: partnerCalc.mrr, platformCost: partnerCalc.platformCost, margin: partnerCalc.margin, breakEven: 0 };
  const extraMRR = mode === 'coach' ? setupFee + packageFee : 0;
  const breakEvenPct = Math.min(100, calc.platformCost > 0 ? Math.round((calc.mrr / calc.platformCost) * 100) : 100);
  const isCovered = calc.mrr >= calc.platformCost;

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-xl w-fit">
        {(['coach', 'partner'] as Mode[]).map((m) => (
          <button key={m} onClick={() => setMode(m)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${mode === m ? 'bg-white shadow text-amber-700' : 'text-gray-500 hover:text-gray-700'}`}>
            {m === 'coach' ? 'Coach Mode' : 'Partner Mode'}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
          <h3 className="font-bold text-gray-800">Your Numbers</h3>
          {mode === 'coach' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">HPOS Plan</label>
                <div className="flex gap-2 flex-wrap">
                  {([['core', 'Core €99/mo'], ['growth_os', 'Growth OS €197/mo']] as [PlanTier, string][]).map(([t, label]) => (
                    <button key={t} onClick={() => setTier(t)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${tier === t ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Active clients</label>
                <input type="number" min={0} value={clients}
                  onChange={(e) => setClients(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Monthly price per client</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {[49, 79, 99, 197].map((p) => (
                    <button key={p} onClick={() => setPrice(p)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${price === p ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                      €{p}
                    </button>
                  ))}
                </div>
                <input type="number" min={0} value={price}
                  onChange={(e) => setPrice(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400" placeholder="Custom price" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Setup fee (optional)</label>
                  <input type="number" min={0} value={setupFee || ''}
                    onChange={(e) => setSetupFee(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400" placeholder="€0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Package fee (optional)</label>
                  <input type="number" min={0} value={packageFee || ''}
                    onChange={(e) => setPackageFee(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400" placeholder="€0" />
                </div>
              </div>
            </>
          ) : (
            <>
              {[
                { label: 'Number of coaches', val: coaches, set: setCoaches },
                { label: 'Monthly fee per coach', val: coachFee, set: setCoachFee },
              ].map(({ label, val, set }) => (
                <div key={label}>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{label}</label>
                  <input type="number" min={0} value={val}
                    onChange={(e) => set(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Avg clients per coach', val: avgClients, set: setAvgClients },
                  { label: 'Client usage fee (€/mo)', val: clientFee, set: setClientFee },
                ].map(({ label, val, set }) => (
                  <div key={label}>
                    <label className="block text-sm font-medium text-gray-600 mb-2">{label}</label>
                    <input type="number" min={0} value={val}
                      onChange={(e) => set(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400" />
                  </div>
                ))}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Certification fee per coach (optional)</label>
                <input type="number" min={0} value={certFee || ''}
                  onChange={(e) => setCertFee(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400" placeholder="€0" />
              </div>
            </>
          )}
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Monthly Revenue" value={fmt(calc.mrr + extraMRR)} sub="MRR" />
            <StatCard label="Annual Revenue" value={fmt((calc.mrr + extraMRR) * 12)} sub="ARR estimate" />
            <StatCard label="Platform Cost" value={fmt(calc.platformCost)} sub="HPOS fees" />
            <StatCard label="Est. Gross Margin" value={fmt(calc.margin + extraMRR)} sub="Before other expenses" />
            {mode === 'partner' && <StatCard label="Upfront Revenue" value={fmt(partnerCalc.upfrontRevenue)} sub="Certification fees" />}
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-700">Platform Cost Coverage</p>
              <span className={`text-sm font-bold ${isCovered ? 'text-green-600' : 'text-amber-600'}`}>
                {isCovered ? 'Covered!' : `${breakEvenPct}%`}
              </span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-3">
              <div className={`h-full rounded-full transition-all duration-500 ${isCovered ? 'bg-green-500' : 'bg-amber-500'}`}
                style={{ width: `${Math.min(100, breakEvenPct)}%` }} />
            </div>
            {mode === 'coach' && (
              <p className="text-xs text-gray-500">
                {isCovered
                  ? `Your current clients cover your ${tier === 'core' ? 'Core' : 'Growth OS'} plan.`
                  : `You need ${coachCalc.breakEven} client${coachCalc.breakEven !== 1 ? 's' : ''} at ${fmt(price)}/month to cover your platform cost.`}
              </p>
            )}
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-600 mb-3">Milestone Targets</p>
            <div className="grid grid-cols-3 gap-3">
              {[1000, 3000, 5000].map((g) => <MilestoneCard key={g} goal={g} current={calc.mrr} price={price} />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Tab: Goal Tracker ----------

function GoalTrackerTab({ savedGoal, userId, onGoalSaved }: {
  savedGoal: ProfitGoal | null;
  userId: string;
  onGoalSaved: (goal: ProfitGoal) => void;
}) {
  const [monthlyGoal, setMonthlyGoal] = useState(1000);
  const [targetDate, setTargetDate] = useState('');
  const [currentClients, setCurrentClients] = useState(0);
  const [price, setPrice] = useState(99);
  const [conversionRate, setConversionRate] = useState(25);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const initialized = useRef(false);

  // Sync form state when savedGoal loads from DB
  useEffect(() => {
    if (savedGoal && !initialized.current) {
      initialized.current = true;
      setMonthlyGoal(savedGoal.monthly_goal ?? 1000);
      setCurrentClients(savedGoal.current_clients ?? 0);
      setPrice(savedGoal.monthly_price ?? 99);
      setTargetDate(savedGoal.target_date ?? '');
    }
  }, [savedGoal]);

  const currentMRR = currentClients * price;
  const gap = Math.max(0, monthlyGoal - currentMRR);
  const clientsNeeded = price > 0 ? Math.ceil(gap / price) : 0;
  const monthsToTarget = targetDate ? Math.max(1, Math.ceil((new Date(targetDate).getTime() - Date.now()) / (30 * 24 * 60 * 60 * 1000))) : 3;
  const newClientsPerMonth = Math.ceil(clientsNeeded / monthsToTarget);
  const callsNeeded = conversionRate > 0 ? Math.ceil((clientsNeeded / conversionRate) * 100) : 0;
  const assessmentsNeeded = callsNeeded * 2;
  const leadsNeeded = assessmentsNeeded * 2;

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      coach_id: userId,
      mode: savedGoal?.mode || 'coach',
      plan_tier: savedGoal?.plan_tier || 'core',
      current_clients: currentClients,
      monthly_price: price,
      monthly_goal: monthlyGoal,
      target_date: targetDate || null,
      audience_type: savedGoal?.audience_type || null,
      hours_per_week: savedGoal?.hours_per_week || null,
      preferred_offer_type: savedGoal?.preferred_offer_type || null,
      generated_strategy: savedGoal?.generated_strategy || null,
      generated_action_plan: savedGoal?.generated_action_plan || null,
    };
    const result = savedGoal?.id
      ? await supabase.from('profit_goals').update(payload).eq('id', savedGoal.id).select().single()
      : await supabase.from('profit_goals').insert(payload).select().single();
    setSaving(false);
    if (!result.error && result.data) {
      onGoalSaved(result.data as ProfitGoal);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  return (
    <div className="space-y-8">
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
          <h3 className="font-bold text-gray-800">Set Your Revenue Goal</h3>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Monthly revenue goal (€)</label>
            <input type="number" min={0} value={monthlyGoal}
              onChange={(e) => setMonthlyGoal(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Target date</label>
            <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Current active clients</label>
            <input type="number" min={0} value={currentClients}
              onChange={(e) => setCurrentClients(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Monthly price per client (€)</label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {[49, 79, 99, 197].map((p) => (
                <button key={p} onClick={() => setPrice(p)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${price === p ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                  €{p}
                </button>
              ))}
            </div>
            <input type="number" min={0} value={price}
              onChange={(e) => setPrice(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Call-to-client conversion rate estimate</label>
            <div className="flex gap-2 flex-wrap">
              {[15, 25, 33, 50].map((r) => (
                <button key={r} onClick={() => setConversionRate(r)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${conversionRate === r ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                  {r}%
                </button>
              ))}
            </div>
          </div>

          <button onClick={handleSave} disabled={saving}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {saving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Saving…</> : saved ? <><CheckCircle className="w-4 h-4" /> Saved!</> : 'Save Goal'}
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <p className="text-sm font-semibold text-amber-700 mb-1">Your Goal Summary</p>
            <p className="text-xl font-bold text-amber-900 leading-snug">
              You need <span className="text-green-600">{clientsNeeded}</span> more client{clientsNeeded !== 1 ? 's' : ''} at {fmt(price)}/month to reach {fmt(monthlyGoal)}/month
              {targetDate ? ` by ${new Date(targetDate).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}` : ''}.
            </p>
            {targetDate && clientsNeeded > 0 && (
              <p className="text-sm text-amber-700 mt-2">
                That's approximately <strong>{newClientsPerMonth}</strong> new client{newClientsPerMonth !== 1 ? 's' : ''} per month for the next {monthsToTarget} month{monthsToTarget !== 1 ? 's' : ''}.
              </p>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <p className="text-sm font-bold text-gray-700 mb-1">Pipeline Reverse-Engineering</p>
            <p className="text-xs text-gray-400 mb-4">Based on {conversionRate}% call-to-client conversion</p>
            <div className="space-y-3">
              {[
                { label: 'Leads needed', value: leadsNeeded, color: 'text-orange-500' },
                { label: 'Assessment completions', value: assessmentsNeeded, color: 'text-blue-500' },
                { label: 'Insight calls needed', value: callsNeeded, color: 'text-teal-500' },
                { label: 'Clients to enrol', value: clientsNeeded, color: 'text-green-600' },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-sm text-gray-600">{label}</span>
                  <span className={`text-sm font-bold ${color}`}>{value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Current MRR" value={fmt(currentMRR)} />
            <StatCard label="Revenue Gap" value={fmt(gap)} sub={`${clientsNeeded} clients needed`} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Tab: AI Profit Coach ----------

function AIProfitCoachTab({ savedGoal, onStrategyGenerated }: {
  savedGoal: ProfitGoal | null;
  onStrategyGenerated: (strategy: string, actionPlan: string) => void;
}) {
  const [audienceType, setAudienceType] = useState('');
  const [hoursPerWeek, setHoursPerWeek] = useState(5);
  const [preferredOffer, setPreferredOffer] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [strategy, setStrategy] = useState('');
  const [actionPlan, setActionPlan] = useState('');
  const initialized = useRef(false);

  useEffect(() => {
    if (savedGoal && !initialized.current) {
      initialized.current = true;
      setAudienceType(savedGoal.audience_type || '');
      setHoursPerWeek(savedGoal.hours_per_week || 5);
      setPreferredOffer(savedGoal.preferred_offer_type || '');
      setStrategy(savedGoal.generated_strategy || '');
      setActionPlan(savedGoal.generated_action_plan || '');
    }
  }, [savedGoal]);

  const hasGoal = !!savedGoal?.monthly_goal;

  const generate = async () => {
    if (!savedGoal) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await callProfitPlan({
        action: 'generate_strategy',
        mode: savedGoal.mode,
        planTier: savedGoal.plan_tier,
        currentClients: savedGoal.current_clients,
        monthlyPrice: savedGoal.monthly_price,
        monthlyGoal: savedGoal.monthly_goal,
        targetDate: savedGoal.target_date,
        audienceType,
        hoursPerWeek,
        preferredOfferType: preferredOffer,
      });
      if (!res.ok) throw new Error('Failed to generate strategy');
      const data = await res.json();
      const newStrategy = data.strategy || data.fullContent || '';
      const newPlan = data.actionPlan || '';
      setStrategy(newStrategy);
      setActionPlan(newPlan);
      onStrategyGenerated(newStrategy, newPlan);

      await supabase.from('profit_goals').update({
        audience_type: audienceType,
        hours_per_week: hoursPerWeek,
        preferred_offer_type: preferredOffer,
        generated_strategy: newStrategy,
        generated_action_plan: newPlan,
      }).eq('id', savedGoal.id);
    } catch {
      setError('Failed to generate strategy. Please try again.');
    }
    setGenerating(false);
  };

  if (!hasGoal) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-4">
          <Target className="w-8 h-8 text-amber-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">Set a goal first</h3>
        <p className="text-gray-500 max-w-sm">Go to the Goal Tracker tab, enter your revenue goal and save it — then come back here to generate your personalised growth strategy.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
        <p className="text-sm font-semibold text-amber-700">
          Your saved goal: <strong>{fmt(savedGoal.monthly_goal)}/month</strong>
          {savedGoal.target_date ? ` by ${new Date(savedGoal.target_date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}` : ''}
        </p>
        <p className="text-xs text-amber-600 mt-1">{savedGoal.current_clients} current clients at {fmt(savedGoal.monthly_price)}/month</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
        <h3 className="font-bold text-gray-800">Additional Context</h3>

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Audience type</label>
          <select value={audienceType} onChange={(e) => setAudienceType(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400 bg-white">
            <option value="">Select audience type…</option>
            <option value="existing_clients">Existing clients / past clients</option>
            <option value="warm_network">Warm network (contacts who know me)</option>
            <option value="cold_audience">Cold audience (social media, ads)</option>
            <option value="mixed">Mixed — I have all of the above</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Hours per week available for client acquisition</label>
          <div className="flex gap-2 flex-wrap">
            {[2, 5, 10, 20].map((h) => (
              <button key={h} onClick={() => setHoursPerWeek(h)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${hoursPerWeek === h ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                {h}h
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Preferred offer type</label>
          <div className="flex gap-2 flex-wrap">
            {['1:1', 'Group', 'Self-led', 'Mixed'].map((t) => (
              <button key={t} onClick={() => setPreferredOffer(t)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${preferredOffer === t ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <button onClick={generate} disabled={generating}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
          {generating ? <><RefreshCw className="w-4 h-4 animate-spin" /> Generating your growth strategy…</> :
            strategy ? <><RefreshCw className="w-4 h-4" /> Regenerate Strategy</> :
              <><Sparkles className="w-4 h-4" /> Generate My Growth Strategy</>}
        </button>

        {error && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-xl p-3">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}
      </div>

      {strategy && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-600" />
            Growth Strategy
          </h3>
          <MarkdownRenderer content={strategy} />
          {actionPlan && (
            <>
              <div className="my-6 border-t border-gray-200" />
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-green-600" />
                30-Day Action Plan
              </h3>
              <MarkdownRenderer content={actionPlan} />
            </>
          )}
          <p className="text-xs text-gray-400 mt-6 italic border-t border-gray-100 pt-4">
            This is a projection based on the numbers you provided, not a guarantee of income. Your actual results depend on your audience, offer, follow-up, and sales activity.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------- Tab: Offer Builder ----------

const OFFER_TEMPLATES = [
  { name: 'AI Insight Plan', price: '€49/month', description: 'Assessment access, AI analysis, follow-up questions, basic AI Coach chat.', for: 'Clients who want to understand themselves and get ongoing AI-supported insights.' },
  { name: 'AI Voice Plan', price: '€79–€99/month', description: 'Everything in the Insight Plan plus AI Coach Clone voice support for deeper reflection.', for: 'Clients who prefer spoken interaction and want deeper AI coaching support.' },
  { name: '90-Day Breakthrough Plan', price: '€297–€997 upfront or €99–€297/month', description: 'Full coaching programme with HPOS assessment, AI analysis, progress tracking, and weekly sessions.', for: 'Motivated clients ready for a structured transformation programme.' },
  { name: 'Premium 1:1 Coaching Plan', price: '€1,500–€5,000+', description: 'High-ticket transformational coaching with full HPOS suite, regular sessions, and AI-supported progress.', for: 'High-achievers who want intensive personal coaching and accountability.' },
];

function OfferBuilderTab() {
  const [selectedTemplate, setSelectedTemplate] = useState<typeof OFFER_TEMPLATES[0] | null>(null);
  const [offerName, setOfferName] = useState('');
  const [offerPrice, setOfferPrice] = useState('');
  const [offerDescription, setOfferDescription] = useState('');
  const [offerFor, setOfferFor] = useState('');
  const [offerIncludes, setOfferIncludes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectTemplate = (t: typeof OFFER_TEMPLATES[0]) => {
    setSelectedTemplate(t);
    setOfferName(t.name);
    setOfferPrice(t.price);
    setOfferDescription(t.description);
    setOfferFor(t.for);
    setOfferIncludes('');
    setOutput('');
  };

  const generateCopy = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await callProfitPlan({ action: 'generate_offer_copy', offerName, offerPrice, offerDescription, offerFor, offerIncludes });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setOutput(data.content || '');
    } catch {
      setError('Failed to generate copy. Please try again.');
    }
    setGenerating(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-semibold text-gray-600 mb-3">Choose a template or build your own</p>
        <div className="grid md:grid-cols-2 gap-3">
          {OFFER_TEMPLATES.map((t) => (
            <button key={t.name} onClick={() => selectTemplate(t)}
              className={`text-left p-4 rounded-2xl border transition-all ${selectedTemplate?.name === t.name ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
              <p className="font-semibold text-gray-800 text-sm">{t.name}</p>
              <p className="text-xs text-amber-600 font-medium mt-1">{t.price}</p>
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">{t.description}</p>
            </button>
          ))}
        </div>
      </div>

      {selectedTemplate && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
          <h3 className="font-bold text-gray-800">Build Your Offer</h3>
          {[
            { label: 'Offer name', value: offerName, set: setOfferName },
            { label: 'Price', value: offerPrice, set: setOfferPrice },
            { label: "Who it's for", value: offerFor, set: setOfferFor },
          ].map(({ label, value, set }) => (
            <div key={label}>
              <label className="block text-sm font-medium text-gray-600 mb-2">{label}</label>
              <input value={value} onChange={(e) => set(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400" />
            </div>
          ))}
          {[
            { label: "What's included", value: offerIncludes, set: setOfferIncludes, placeholder: 'e.g. HPOS assessment, weekly 1:1 calls, AI analysis, progress tracking…' },
            { label: 'Offer description', value: offerDescription, set: setOfferDescription, placeholder: 'Brief description of the offer and the outcome' },
          ].map(({ label, value, set, placeholder }) => (
            <div key={label}>
              <label className="block text-sm font-medium text-gray-600 mb-2">{label}</label>
              <textarea value={value} onChange={(e) => set(e.target.value)} rows={3} placeholder={placeholder}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-amber-400 resize-none" />
            </div>
          ))}
          <button onClick={generateCopy} disabled={generating || !offerName}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {generating ? <><RefreshCw className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate Sales Copy</>}
          </button>
          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-xl p-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /><p className="text-sm">{error}</p>
            </div>
          )}
        </div>
      )}

      {output && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800">Generated Sales Copy</h3>
            <button onClick={() => { navigator.clipboard.writeText(output); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              className="flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700 transition-colors">
              {copied ? <><CheckCircle className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy all</>}
            </button>
          </div>
          <MarkdownRenderer content={output} />
        </div>
      )}
    </div>
  );
}

// ---------- Tab: Action Plan ----------

const CAMPAIGNS = [
  { id: 'warm_reactivation', title: 'Warm Reactivation Campaign', description: 'Contact past clients and warm contacts. Invite them to complete the HPOS assessment and book an AI Insight Review call.' },
  { id: 'assessment_lead_magnet', title: 'Assessment Lead Magnet Campaign', description: 'Use your HPOS Lead Page to offer a free assessment. Drive traffic and follow up with Insight calls.' },
  { id: 'ai_insight_call', title: 'AI Insight Review Call Campaign', description: 'Invite 10 people to a 45-minute call where you walk through their HPOS results and AI analysis, then pitch your coaching offer.' },
  { id: '7_day_challenge', title: '7-Day Challenge Campaign', description: 'Run a 7-day personal development challenge using HPOS assessment on Day 1 and pitch a coaching offer on Day 7.' },
  { id: 'partner_cohort', title: 'Partner Founding Cohort Campaign', description: 'Recruit coaches, trainers, or practitioners into a founding HPOS certification cohort at €997–€1,997 upfront.' },
  { id: 'local_practitioner', title: 'Local Practitioner Campaign', description: 'Target local wellness professionals, HR consultants, or business coaches and run an in-person or virtual assessment evening.' },
];

function ActionPlanTab({ savedGoal, generatedActionPlan }: {
  savedGoal: ProfitGoal | null;
  generatedActionPlan: string;
}) {
  const [campaignOutputs, setCampaignOutputs] = useState<Record<string, string>>({});
  const [generating, setGenerating] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const displayPlan = generatedActionPlan || savedGoal?.generated_action_plan || '';

  const generateCampaign = async (campaignId: string) => {
    setGenerating(campaignId);
    setErrors((prev) => ({ ...prev, [campaignId]: '' }));
    try {
      const res = await callProfitPlan({
        action: 'generate_campaign_plan',
        campaignType: CAMPAIGNS.find((c) => c.id === campaignId)?.title,
        mode: savedGoal?.mode || 'coach',
        planTier: savedGoal?.plan_tier || 'core',
        monthlyGoal: savedGoal?.monthly_goal || 1000,
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setCampaignOutputs((prev) => ({ ...prev, [campaignId]: data.content || '' }));
    } catch {
      setErrors((prev) => ({ ...prev, [campaignId]: 'Failed to generate. Try again.' }));
    }
    setGenerating(null);
  };

  return (
    <div className="space-y-8">
      {displayPlan ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-green-600" />
            Your 30-Day Action Plan
          </h3>
          <MarkdownRenderer content={displayPlan} />
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-start gap-4">
          <Lightbulb className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">No action plan yet</p>
            <p className="text-sm text-amber-700 mt-1">Set your goal in the Goal Tracker tab, then generate your growth strategy in the AI Profit Coach tab. Your 30-day action plan will appear here.</p>
          </div>
        </div>
      )}

      <div>
        <h3 className="font-bold text-gray-800 mb-1">Campaign Library</h3>
        <p className="text-sm text-gray-500 mb-4">Choose a campaign and get a step-by-step plan with scripts and expected results.</p>
        <div className="space-y-4">
          {CAMPAIGNS.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-start justify-between gap-4 mb-2">
                <div>
                  <p className="font-semibold text-gray-800">{c.title}</p>
                  <p className="text-sm text-gray-500 mt-1">{c.description}</p>
                </div>
                <button onClick={() => generateCampaign(c.id)} disabled={generating === c.id}
                  className="flex-shrink-0 flex items-center gap-2 bg-amber-50 hover:bg-amber-100 text-amber-700 font-semibold text-sm px-4 py-2 rounded-xl transition-colors disabled:opacity-50">
                  {generating === c.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                  Get Plan
                </button>
              </div>
              {errors[c.id] && <p className="text-xs text-red-500 mt-2">{errors[c.id]}</p>}
              {campaignOutputs[c.id] && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <MarkdownRenderer content={campaignOutputs[c.id]} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Tab: Business Growth AI Chat ----------

const STARTER_PROMPTS = [
  "What's the fastest way to reach my revenue goal?",
  "Help me price my coaching offer",
  "Write me a DM invite script for past clients",
  "What campaign should I run first?",
];

function BusinessGrowthAIChatTab({ savedGoal }: { savedGoal: ProfitGoal | null }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const welcomeMessage = savedGoal?.monthly_goal
    ? `Hi! I'm your HPOS Business Growth Coach. I can see your goal is ${fmt(savedGoal.monthly_goal)}/month${savedGoal.target_date ? ` by ${new Date(savedGoal.target_date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}` : ''}. You currently have ${savedGoal.current_clients} client${savedGoal.current_clients !== 1 ? 's' : ''} at ${fmt(savedGoal.monthly_price)}/month.\n\nI can help with revenue strategy, offer pricing, client acquisition, campaign planning, and anything else to grow your coaching business. What would you like to work on?`
    : `Hi! I'm your HPOS Business Growth Coach. I'm here to help you build a profitable coaching business using NLP OS.\n\nI can help you calculate revenue targets, price your offers, plan client acquisition campaigns, write DM scripts, and create a growth strategy. What would you like to work on?`;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    const userMsg: ChatMessage = { id: crypto.randomUUID(), role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const res = await callProfitPlan({
        action: 'chat',
        message: trimmed,
        conversation_history: history,
        context: savedGoal ? {
          mode: savedGoal.mode,
          planTier: savedGoal.plan_tier,
          currentClients: savedGoal.current_clients,
          monthlyPrice: savedGoal.monthly_price,
          monthlyGoal: savedGoal.monthly_goal,
          targetDate: savedGoal.target_date,
          audienceType: savedGoal.audience_type,
          generatedStrategy: savedGoal.generated_strategy ? '[Strategy has been generated]' : null,
        } : null,
      });
      if (!res.ok) throw new Error('Failed to get response');
      const data = await res.json();
      const reply = data.reply || data.content || '';
      const assistantMsg: ChatMessage = { id: crypto.randomUUID(), role: 'assistant', content: reply };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch {
      setError('Failed to get a response. Please try again.');
    }
    setLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <div className="flex flex-col h-[680px]">
      {/* Welcome banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 flex-shrink-0">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-800">HPOS Business Growth Coach</p>
            <p className="text-sm text-amber-700 mt-0.5 leading-relaxed">{welcomeMessage}</p>
          </div>
        </div>
      </div>

      {/* Starter prompts (only before first user message) */}
      {messages.length === 0 && (
        <div className="flex flex-wrap gap-2 mb-4 flex-shrink-0">
          {STARTER_PROMPTS.map((p) => (
            <button key={p} onClick={() => sendMessage(p)}
              className="text-sm border border-gray-200 bg-white hover:border-amber-400 hover:bg-amber-50 text-gray-700 hover:text-amber-700 px-3 py-2 rounded-xl transition-all">
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 pb-4 min-h-0">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === 'user' ? 'bg-amber-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
              {m.role === 'assistant' ? (
                <MarkdownRenderer content={m.content} />
              ) : (
                <p className="text-sm leading-relaxed">{m.content}</p>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-amber-600 animate-spin" />
              <span className="text-sm text-gray-500">Thinking…</span>
            </div>
          </div>
        )}
        {error && (
          <div className="flex justify-center">
            <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex gap-3 items-end flex-shrink-0 pt-3 border-t border-gray-100">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about revenue, offers, campaigns, pricing…"
          rows={2}
          className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-amber-400 resize-none"
        />
        <button onClick={() => sendMessage(input)} disabled={!input.trim() || loading}
          className="bg-amber-600 hover:bg-amber-700 text-white p-3 rounded-xl transition-colors disabled:opacity-50 flex-shrink-0">
          <Send className="w-5 h-5" />
        </button>
      </div>
      <p className="text-xs text-gray-400 mt-2 text-center">Press Enter to send · Shift+Enter for new line</p>
    </div>
  );
}

// ---------- Main component ----------

const TABS: { id: TabId; label: string }[] = [
  { id: 'planner', label: 'Revenue Planner' },
  { id: 'goals', label: 'Goal Tracker' },
  { id: 'ai-coach', label: 'AI Profit Coach' },
  { id: 'offer-builder', label: 'Offer Builder' },
  { id: 'action-plan', label: 'Action Plan' },
  { id: 'ai-chat', label: 'Business Growth AI Coach' },
];

function ProfitDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('planner');
  const [savedGoal, setSavedGoal] = useState<ProfitGoal | null>(null);
  const [loading, setLoading] = useState(true);
  // Lifted state so Action Plan tab gets content immediately after generation
  const [generatedStrategy, setGeneratedStrategy] = useState('');
  const [generatedActionPlan, setGeneratedActionPlan] = useState('');

  const loadGoal = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profit_goals')
      .select('*')
      .eq('coach_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    const goal = data as ProfitGoal | null;
    setSavedGoal(goal);
    if (goal?.generated_strategy) setGeneratedStrategy(goal.generated_strategy);
    if (goal?.generated_action_plan) setGeneratedActionPlan(goal.generated_action_plan);
    setLoading(false);
  }, [user]);

  useEffect(() => { loadGoal(); }, [loadGoal]);

  const handleStrategyGenerated = (strategy: string, actionPlan: string) => {
    setGeneratedStrategy(strategy);
    setGeneratedActionPlan(actionPlan);
    setSavedGoal((prev) => prev ? { ...prev, generated_strategy: strategy, generated_action_plan: actionPlan } : prev);
  };

  if (loading) return <LoadingSpinner message="Loading Profit Dashboard…" />;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
          <TrendingUp className="w-7 h-7 text-green-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Profit</h1>
          <p className="text-gray-500 text-sm mt-0.5">Your HPOS Growth Command Centre — calculate your numbers, set your goal, and get an AI-guided plan to grow your coaching business.</p>
        </div>
      </div>

      {/* Tabs — scrollable on small screens */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl overflow-x-auto">
        {TABS.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.id ? 'bg-white shadow text-amber-700' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'planner' && <RevenuePlannerTab />}
      {activeTab === 'goals' && user && (
        <GoalTrackerTab savedGoal={savedGoal} userId={user.id} onGoalSaved={(goal) => setSavedGoal(goal)} />
      )}
      {activeTab === 'ai-coach' && (
        <AIProfitCoachTab savedGoal={savedGoal} onStrategyGenerated={handleStrategyGenerated} />
      )}
      {activeTab === 'offer-builder' && <OfferBuilderTab />}
      {activeTab === 'action-plan' && (
        <ActionPlanTab savedGoal={savedGoal} generatedActionPlan={generatedActionPlan} />
      )}
      {activeTab === 'ai-chat' && <BusinessGrowthAIChatTab savedGoal={savedGoal} />}
    </div>
  );
}


export { ProfitDashboard }